import DashboardModal from "app/components/dashboard-modal/dashboard-modal";
import PageWrapper from "app/components/page-wrapper/page-wrapper";
import { alphaLinks, dashboardTexts, welcomeBanner } from "app/config";
import useUTMTracking from "app/hooks/useUtmLinksTracker";
import useWidth from "app/hooks/useWidth";
import { useTheme } from "app/shared";
import AccountWidget from "app/widgets/account-widget/account-widget";
import HeaderConnect from "app/widgets/crypto-header/header-connect";
import DashboardButtons from "app/widgets/dashboard-buttons/dashboard-buttons";
import DashboardKarma from "app/widgets/dashboard-karma/dashboard-karma";
import ReferralCard from "app/widgets/refferal-card/refferal-card";
import TierWidget from "app/widgets/tier-widget/tier-widget";
import WelcomeBanner from "app/widgets/welcome-banner/welcome-banner";
import { FC, useEffect, useState } from "react";
import { campaignIds } from "app/enums";
import IDOTracker from "app/widgets/IdoTracker/IdoTracker";
import NFTWidget, { NFTCard } from "app/widgets/NFTWidget/NFTWidget";
import {
  useGetAllRoulettesQuery,
  useGetAlphapionerQuery,
  useGetClipFinanceBalanceQuery,
  useGetLineXnftQuery,
  useGetMindBalanceQuery,
  useGetMindNftQuery,
  useGetMrUniNftQuery,
  useGetNftTokensQuery,
  useGetPastIdosQuery,
  useGetUpcomingIdosQuery,
  useGetUserKarmaQuery,
  useGetUserLeaderboardQuery,
  useGetUserRefsQuery,
  useGetWhitelistedIdosQuery,
  useLazyGetClaimrTokenQuery,
} from "store/api/new.api";
import { useAccount } from "wagmi";
import { LiFi } from "../widgets/LiFi/LiFi";
import CaseOpener from "../widgets/WheelOfFortune/CaseOpener";
import { INTEGRATOR } from "../utils/constants";

import styles from "./dashboard.page.module.scss";

const DashboardPage: FC = () => {
  useUTMTracking();
  const { isMobile } = useWidth();
  const { address } = useAccount();
  const userAddress = address || "";
  const account = address || "";
  const isAuth = !!account;
  const [modalIsOpen, setRestrictedModal] = useState<boolean>(false);
  const [claimrLoading, setClaimrLoading] = useState<boolean>(true);
  const { theme } = useTheme();

  const { data: upcomingIdos, isLoading: isUpcomingLoading } =
    useGetUpcomingIdosQuery({ page: 1, limit: 20 });
  const { data: whitelistedIdos, isLoading: isWhitelistedLoading } =
    useGetWhitelistedIdosQuery({ page: 1, limit: 20 });
  const { data: pastIdos, isLoading: isPastLoading } = useGetPastIdosQuery({
    page: 1,
    limit: 20,
  });

  const { data: roulettes } = useGetAllRoulettesQuery();

  const [rouletteId, setRouletteId] = useState<string | null>(null);

  useEffect(() => {
    roulettes && roulettes.length > 0
      ? setRouletteId(roulettes[roulettes.length - 1]._id)
      : setRouletteId(null);
  }, [roulettes]);

  //@ts-ignore
  const {
    data: balance,
    error: _error,
    isLoading: isBalanceLoading,
  } = useGetMindBalanceQuery(undefined);

  const [combinedNfts, setCombinedNfts] = useState<NFTCard[]>([]);
  //@ts-ignore
  const { data: nft, isLoading: isMindLoading } = useGetMindNftQuery(undefined);
  //@ts-ignore
  const { data: lineanft, isLoading: isLineaLoading } =
    useGetLineXnftQuery(undefined);
  //@ts-ignore
  const { data: clip, isLoading: isClipLoading } =
    useGetClipFinanceBalanceQuery(undefined);
  //@ts-ignore
  const { data: alphapioner, isLoading: isAlphapionerLoading } =
    useGetAlphapionerQuery(undefined);
  //@ts-ignore
  const { data: mrUni, isLoading: mrUniLoading } =
    useGetMrUniNftQuery(undefined);
  const { data: unicorn, isLoading: isUnicornLoading } = useGetNftTokensQuery({
    userAddress,
  });

  useEffect(() => {
    if (
      !isMindLoading &&
      !isLineaLoading &&
      !isClipLoading &&
      !isUnicornLoading &&
      !isAlphapionerLoading
    ) {
      const nfts = [
        ...(nft || []),
        ...(lineanft || []),
        ...(clip || []),
        ...(alphapioner || []),
        ...(mrUni || []),
        //@ts-ignore
        ...(unicorn?.data?.minted
          ? [
              {
                title: "Early Supporter Unicorn",
                image: "https://media.alphamind.co/nft/unicorn/unicorn.png",
              },
            ]
          : []),
      ];

      setCombinedNfts(nfts);
    }
  }, [
    nft,
    lineanft,
    clip,
    unicorn,
    isMindLoading,
    isLineaLoading,
    mrUniLoading,
    isClipLoading,
    isUnicornLoading,
  ]);

  const { data: karma, isLoading: karmaLoading } = useGetUserKarmaQuery(
    {
      account,
      campaignIds,
    },
    {
      skip: !account,
    }
  );

  useEffect(() => {
    console.log('[Dashboard] useGetUserKarmaQuery state:', {
      account,
      campaignIds,
      hasAccount: !!account,
      karmaLoading,
      hasKarma: !!karma,
      karmaData: karma,
    });
  }, [account, karmaLoading, karma]);
  const { data: refs, isLoading: isLoadingRefs } = useGetUserRefsQuery({
    account,
    campaignIds,
  });
  //@ts-ignore
  const { data: userTier, isLoading: userTierLoading } =
    useGetUserLeaderboardQuery({
      platform: "web3",
      address: address as string,
      campaignIds,
    });

  const [triggerGetClaimrTokenQuery, { data: claimrToken }] =
    useLazyGetClaimrTokenQuery();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isAuth && address) {
      triggerGetClaimrTokenQuery({ wallet: address });
    }
  }, [isAuth, address, triggerGetClaimrTokenQuery]);

  useEffect(() => {
    if (claimrToken) {
      const timer = setTimeout(() => {
        setClaimrLoading(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [claimrToken]);

  const redirectToAlphamind = () => {
    window.location.href = alphaLinks.HOME;
  };

  const closeModal = () => setRestrictedModal(false);

  return (
    <PageWrapper>
      <div className={`${styles.main} ${theme === "dark" && styles.darkTheme}`}>
        {!isAuth && (
          <div className={styles.connector}>
            <h3 className={styles.connectTitle}>{dashboardTexts.title}</h3>
            <p className={styles.connectText}>{dashboardTexts.text}</p>
            <h1 className={styles.connectHeader}>{dashboardTexts.header}</h1>
            <HeaderConnect />
          </div>
        )}
        {isAuth && (
          <div className={styles.accountContainer}>
            <WelcomeBanner data={welcomeBanner} />
            <CaseOpener _id={rouletteId} />
            <DashboardButtons />
            <AccountWidget
              balanceLoading={isBalanceLoading}
              karma={karma?.karmaView}
              tier={userTier?.tier}
              karmaLoading={karmaLoading}
              tierLoading={userTierLoading}
              balance={balance?.mind}
            />
            <div className={styles.dashboardWrapper}>
              {!isMobile && (
                <>
                  <div className={styles.leftSide}>
                    <DashboardKarma
                      leaderboardRank={userTier?.position}
                      totalKarma={karma?.karmaView}
                      referralKarma={userTier?.ref_xp}
                      discordKarma={2400}
                      karmaLoading={karmaLoading}
                      refferalLoading={userTierLoading}
                      leaderboardLoading={userTierLoading}
                    />
                    <ReferralCard
                      refferals={refs}
                      isLoadingRefs={isLoadingRefs}
                    />
                  </div>
                  <div className={styles.rightSide}>
                    <IDOTracker
                      pastIDOs={pastIdos}
                      whitelistedIDOs={whitelistedIdos}
                      newIDOs={upcomingIdos}
                    />
                    <TierWidget
                      isLoading={userTierLoading}
                      userTier={userTier?.tier}
                    />
                    <LiFi
                      variant="compact"
                      integrator={INTEGRATOR}
                      tier={userTier?.tier}
                    />
                    <NFTWidget nfts={combinedNfts} />
                  </div>
                </>
              )}
              {isMobile && (
                <>
                  <DashboardKarma
                    leaderboardRank={userTier?.position}
                    totalKarma={karma?.karmaView}
                    referralKarma={userTier?.ref_xp}
                    discordKarma={2400}
                    karmaLoading={karmaLoading}
                    refferalLoading={userTierLoading}
                    leaderboardLoading={userTierLoading}
                  />
                  <IDOTracker
                    pastIDOs={pastIdos}
                    whitelistedIDOs={whitelistedIdos}
                    newIDOs={upcomingIdos}
                  />
                  <TierWidget
                    isLoading={userTierLoading}
                    userTier={userTier?.tier}
                  />
                  <ReferralCard
                    refferals={refs}
                    isLoadingRefs={isLoadingRefs}
                  />
                  <LiFi
                    tier={userTier?.tier}
                    variant="compact"
                    integrator={INTEGRATOR}
                  />
                  <NFTWidget nfts={combinedNfts} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <DashboardModal
        closeModal={closeModal}
        isOpen={modalIsOpen}
        redirectToAlphamind={redirectToAlphamind}
      />
    </PageWrapper>
  );
};

export default DashboardPage;
